import { useMutation } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { useCallback, useMemo, useState } from 'react'
import { FileTypeEnum } from 'utils'

import useFilePicker from 'components/finder-v2/useFilePicker'
import { useUppy } from 'components/finder-v2/useUppy'
import Form from 'components/formKit/Form'
import FormField from 'components/formKit/FormField'
import FormButtonSubmit from 'components/formKit/components/FormButtonSubmit'
import FieldInput from 'components/formKit/fields/FieldInput'
import FieldSelect from 'components/formKit/fields/FieldSelect'
import { genFieldUnsafe } from 'components/formKit/genConfig'
import Button from 'components/uiKit/Button'
import Checkbox from 'components/uiKit/Checkbox'
import { KitSize } from 'components/uiKit/KitTypes'
import type { MCWithParams } from 'components/uiKit/Modal'
import Modal, { ModalBody, ModalFooter } from 'components/uiKit/Modal'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import Segmented from 'components/uiKit/Segmented'
import { FileMetaFor, ImportBrandingStrategy } from 'gql/__generated__/graphql'
import { useGetAllBrandSelectItems } from 'gql/brands/apollo'
import { useFilesSubscription } from 'gql/files/apollo'
import { projectGroupAll } from 'gql/projectGroups/gql/queries'
import { importProjectFromScorm } from 'gql/projects/gql/mutations'
import { t } from 'services/Translation'
import type { UppySucssesfull } from 'store/models'
import { getEnumOption } from 'utils/enum'

import * as s from './ProjectImportModal.module.scss'

const STORAGE_KEY_PREFIX = 'projectImport_'

const getStorageKey = (companyId: string) => `${STORAGE_KEY_PREFIX}${companyId}`

const getSavedSettings = (companyId: string) => {
  try {
    const saved = localStorage.getItem(getStorageKey(companyId))
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

const saveSettings = (
  companyId: string,
  settings: { brandId?: string; targetFolderId?: string },
) => {
  try {
    const current = getSavedSettings(companyId)
    localStorage.setItem(getStorageKey(companyId), JSON.stringify({ ...current, ...settings }))
  } catch {
    // ignore
  }
}

interface IProjectImportModalParams {
  companyId: string
  categoryId: string
  fileMetaIds?: string[]
}

type ProjectImportFormData = {
  url: string
  brandId?: string
  targetFolderId?: string
}

type ImportMode = 'url' | 'file'

const IMPORT_MODE_OPTIONS = [
  { value: 'url' as const, label: t('modal.projectImport.mode.url') },
  { value: 'file' as const, label: t('modal.projectImport.mode.file') },
]

const BRANDING_STRATEGY_OPTIONS = [
  getEnumOption('ImportBrandingStrategy', ImportBrandingStrategy.IMPORT),
  getEnumOption('ImportBrandingStrategy', ImportBrandingStrategy.USE_COMPANY),
]

const FILE_PICKER_OPTIONS = {
  fileTypes: [FileTypeEnum.TEXT],
  createdFor: FileMetaFor.owner,
  multiple: false,
  name: 'scormPackage',
}

const urlField = genFieldUnsafe({
  name: 'url',
  as: FieldInput,
  label: t('modal.projectImport.urlLabel'),
  props: {
    placeholder: t('modal.projectImport.urlPlaceholder'),
  },
})

const ProjectImportModal: MCWithParams<IProjectImportModalParams> = ({ onClose, params }) => {
  const hasPreselectedFiles = params.fileMetaIds && params.fileMetaIds.length > 0
  const savedSettings = useMemo(() => getSavedSettings(params.companyId), [params.companyId])

  const [isLoading, setIsLoading] = useState(false)
  const [importMode, setImportMode] = useState<ImportMode>('file')
  const [uploadedFileMetaId, setUploadedFileMetaId] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [brandingStrategy, setBrandingStrategy] = useState<ImportBrandingStrategy>(
    ImportBrandingStrategy.USE_COMPANY,
  )
  const [skipPartialMigrations, setSkipPartialMigrations] = useState(false)

  const { data: brandsData } = useGetAllBrandSelectItems({ companyId: params.companyId })

  const folderField = genFieldUnsafe({
    name: 'targetFolderId',
    as: FieldInput,
    label: t('modal.projectImport.folderLabel'),
    props: {
      placeholder: t('modal.projectImport.folderPlaceholder'),
    },
  })

  const brandsOptions = useMemo(
    () =>
      brandsData?.data?.brands?.map((brand) => ({
        value: brand.id,
        label: brand.name,
      })) || [],
    [brandsData],
  )

  const brandField = useMemo(
    () =>
      genFieldUnsafe({
        name: 'brandId',
        as: FieldSelect<string>,
        label: t('modal.projectImport.brandLabel'),
        props: {
          options: brandsOptions,
          placeholder: t('modal.projectImport.brandPlaceholder'),
          isSearch: true,
        },
      }),
    [brandsOptions],
  )

  const initialValues: ProjectImportFormData = useMemo(
    () => ({
      url: '',
      brandId: savedSettings.brandId,
      targetFolderId: hasPreselectedFiles
        ? savedSettings.targetFolderId || ''
        : params.categoryId || '',
    }),
    [savedSettings.brandId, savedSettings.targetFolderId, hasPreselectedFiles, params.categoryId],
  )

  const [importProject] = useMutation(importProjectFromScorm, {
    refetchQueries: [String(getOperationName(projectGroupAll))],
  })

  const uppy = useUppy((_uppy, _result, data) =>
    data.forEach((item) =>
      item.successful.forEach((file: UppySucssesfull<{ data?: { name?: string } }>) => {
        if (file.meta.fieldName === FILE_PICKER_OPTIONS.name) {
          setUploadedFileMetaId(file.meta.fileMetaId)
          setUploadedFileName(file.data?.name || 'package.zip')
        }
      }),
    ),
  )

  useFilesSubscription({ companyId: params.companyId, parentId: null })

  const { onClickUploadFile } = useFilePicker({
    uppy,
    ...FILE_PICKER_OPTIONS,
    params: { companyId: params.companyId },
  })

  const handleSubmit = useCallback(
    async (values: ProjectImportFormData) => {
      // Save settings to localStorage
      if (brandingStrategy === ImportBrandingStrategy.USE_COMPANY && values.brandId) {
        saveSettings(params.companyId, { brandId: values.brandId })
      }
      saveSettings(params.companyId, { targetFolderId: values.targetFolderId })
      const brandId =
        brandingStrategy === ImportBrandingStrategy.USE_COMPANY ? values.brandId : undefined

      const folderId = values.targetFolderId?.trim()
      const targetProjectGroupId = folderId && folderId !== 'root' ? folderId : null

      if (hasPreselectedFiles) {
        setIsLoading(true)
        try {
          for (const fileMetaId of params.fileMetaIds!) {
            const result = await importProject({
              variables: {
                data: {
                  fileMetaId,
                  companyId: params.companyId,
                  targetProjectGroupId,
                  brandingStrategy,
                  brandId,
                  skipPartialMigrations,
                },
              },
            })

            if (result.data?.importProjectFromScorm?.project) {
              notify({
                type: NotificationType.success,
                message: t('modal.projectImport.success'),
              })
            }
          }
          onClose(true)
        } catch (error) {
          notify({
            type: NotificationType.error,
            message: t('modal.projectImport.error.importFailed'),
          })
        } finally {
          setIsLoading(false)
        }
        return
      }

      if (importMode === 'url' && !values.url.trim()) {
        notify({
          type: NotificationType.error,
          message: t('modal.projectImport.error.urlRequired'),
        })
        return
      }

      if (importMode === 'file' && !uploadedFileMetaId) {
        notify({
          type: NotificationType.error,
          message: t('modal.projectImport.error.fileRequired'),
        })
        return
      }

      setIsLoading(true)

      try {
        const result = await importProject({
          variables: {
            data: {
              url: importMode === 'url' ? values.url.trim() : undefined,
              fileMetaId: importMode === 'file' ? uploadedFileMetaId : undefined,
              companyId: params.companyId,
              targetProjectGroupId,
              brandingStrategy,
              brandId,
              skipPartialMigrations,
            },
          },
        })

        if (result.data?.importProjectFromScorm?.project) {
          notify({
            type: NotificationType.success,
            message: t('modal.projectImport.success'),
          })
          onClose(result.data.importProjectFromScorm.project)
        }
      } catch (error) {
        notify({
          type: NotificationType.error,
          message: t('modal.projectImport.error.importFailed'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      hasPreselectedFiles,
      importMode,
      uploadedFileMetaId,
      importProject,
      onClose,
      params.companyId,
      params.fileMetaIds,
      brandingStrategy,
      skipPartialMigrations,
    ],
  )

  const handleModeChange = (mode: ImportMode) => {
    setImportMode(mode)
    setUploadedFileMetaId(null)
    setUploadedFileName(null)
  }

  const canSubmit =
    hasPreselectedFiles ||
    (importMode === 'url' && initialValues.url !== '') ||
    (importMode === 'file' && uploadedFileMetaId !== null)

  return (
    <Form initialValues={initialValues} name='projectImport' onSubmit={handleSubmit}>
      <Modal name='projectImportModal' title={t('modal.projectImport.title')} width={700}>
        <ModalBody sizeAutoCapable>
          <div className={s.form}>
            {/* File source section */}
            {hasPreselectedFiles ? (
              <div className={s.fileSection}>
                <p className={s.sectionLabel}>{t('modal.projectImport.source')}</p>
                <p className={s.hint}>
                  {t('modal.projectImport.filesSelected', { count: params.fileMetaIds!.length })}
                </p>
              </div>
            ) : (
              <>
                <Segmented
                  name='importMode'
                  onChange={handleModeChange}
                  options={IMPORT_MODE_OPTIONS}
                  size={KitSize.S}
                  value={importMode}
                />

                {importMode === 'url' && (
                  <>
                    <FormField config={urlField} />
                    <p className={s.hint}>{t('modal.projectImport.urlHint')}</p>
                  </>
                )}

                {importMode === 'file' && (
                  <div className={s.fileSection}>
                    {uploadedFileName ? (
                      <div className={s.uploadedFile}>
                        <span className={s.fileName}>{uploadedFileName}</span>
                        <Button
                          name='changeFile'
                          onClick={onClickUploadFile}
                          size={KitSize.S}
                          styleType='ghost'
                        >
                          {t('uiKit.button.change')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        name='selectFile'
                        onClick={onClickUploadFile}
                        size={KitSize.S}
                        styleType='secondary'
                      >
                        {t('modal.projectImport.selectFile')}
                      </Button>
                    )}
                    <p className={s.hint}>{t('modal.projectImport.fileHint')}</p>
                  </div>
                )}
              </>
            )}

            {/* Branding section - always shown */}
            <div className={s.brandingSection}>
              <p className={s.sectionLabel}>{t('modal.projectImport.branding.label')}</p>
              <Segmented
                name='brandingStrategy'
                onChange={setBrandingStrategy}
                options={BRANDING_STRATEGY_OPTIONS}
                size={KitSize.S}
                value={brandingStrategy}
              />
              {brandingStrategy === ImportBrandingStrategy.USE_COMPANY && (
                <div className={s.brandSelect}>
                  <FormField config={brandField} />
                </div>
              )}
            </div>

            {/* Folder section */}
            <div className={s.folderSection}>
              {hasPreselectedFiles ? (
                <FormField config={folderField} />
              ) : (
                <>
                  <p className={s.sectionLabel}>{t('modal.projectImport.folderLabel')}</p>
                  <p className={s.folderName}>
                    {params.categoryId || t('common.projectsRootDirectory')}
                  </p>
                </>
              )}
            </div>

            {/* Advanced options */}
            <div>
              <Checkbox
                name='skipPartialMigrations'
                onChange={setSkipPartialMigrations}
                value={skipPartialMigrations}
              >
                {t('modal.projectImport.skipPartialMigrations')}
              </Checkbox>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button name='cancelProjectImport' onClick={() => onClose()} styleType='secondary'>
            {t('uiKit.button.cancel')}
          </Button>
          <FormButtonSubmit
            disabled={isLoading || !canSubmit}
            label={t('modal.projectImport.submit')}
            loading={isLoading}
            name='submitProjectImport'
          />
        </ModalFooter>
      </Modal>
    </Form>
  )
}

export default ProjectImportModal
